import React from "react";
import { connect } from "react-redux";
import { applySpec, find, partial, path, propEq, propOr } from "ramda";
import {
  mediaItemFieldChanged,
  blurMediaItemField,
  showEditThumbnailDialog,
} from "edit-media/redux/editMediaActions";
import {
  getEditMediaItem,
  getEditMediaErrors,
  getLanguages,
  getLicenseTypes,
  getThumbnailDialogProcessing,
} from "common/selectors";
import { firstError } from "util/validation";
import { Input } from "react-toolbox/lib/input";
import { getThumbnailUrl } from "util/mediaUtil";
import { Checkbox } from "react-toolbox/lib/checkbox";
import { Button } from "react-toolbox/lib/button";
import { getAutocompleteSource } from "util/componentUtil";
import { Autocomplete } from "react-toolbox/lib/autocomplete";
import styles from "edit-media/components/EditMediaGeneralTab.css";
import ProgressBar from "react-toolbox/lib/progress_bar";

import { KeyboardDatePicker } from "@material-ui/pickers";
// import moment from "moment";

//-- BB-6085 : Adjusting the date since the picker always wants to be in local timezone and all pub dates come back 00:00:00 time in UTC.
// const dateAdjust = date => {
//   const hourShift = Math.ceil(date.getTimezoneOffset() / 60);
//   date.setHours(date.getHours() + hourShift);
//   return date;
// };

const licenseTypeCodePath = media => path(["licenseTypeCode"], media) || "";
const openProp = propOr(false, "open");
const alwaysDownload = (licenseTypeCode, licenseTypes) =>
  openProp(find(propEq("code", licenseTypeCode))(licenseTypes));

const EditMediaGeneralTab = ({
  media,
  languages,
  licenseTypes,
  onFieldChange,
  onFieldBlur,
  onEditThumbnailClicked,
  errors,
  thumbnailProcessing,
}) => {
  const thumbnailUrl = getThumbnailUrl(media);

  return (
    <div className={styles.container}>
      <div>
        {thumbnailProcessing ? (
          <div className={styles.thumbnailProcessing}>
            <ProgressBar
              // theme={accent ? progressBarThemeAccent : progressBarTheme}
              type="circular"
              mode="indeterminate"
            />
          </div>
        ) : (
          <div className={styles.thumbnailContainer}>
            <div>
              <img
                className={styles.thumbnail}
                src={thumbnailUrl}
                alt="Thumbnail"
              />
            </div>
            <Button
              icon="edit"
              label="Edit"
              raised={true}
              onClick={onEditThumbnailClicked}
            />
          </div>
        )}
      </div>
      <div className={styles.fieldsContainer}>
        <Input
          className={styles.field}
          label="Title"
          onChange={onFieldChange(["title"])}
          value={media.title}
          onBlur={partial(onFieldBlur, ["title", media.title])}
          error={firstError(errors, "title")}
        />
        <Input
          className={styles.field}
          label="Subtitle (Optional)"
          onChange={onFieldChange(["subtitle"])}
          value={media.subtitle}
        />
        <Input
          className={styles.field}
          label="Series (Optional)"
          onChange={onFieldChange(["series"])}
          value={media.series}
        />
        <Autocomplete
          className={styles.field}
          direction="down"
          label="Language"
          multiple={false}
          showSuggestionsWhenValueIsSet={true}
          onChange={onFieldChange(["languageIso639_2"])}
          source={getAutocompleteSource(languages)}
          value={path(["languageIso639_2"], media) || ""}
        />
        <div className={styles.dateContainer}>
          <KeyboardDatePicker
            disableToolbar
            autoOk
            id="publication-date"
            format="MM/dd/yyyy"
            minDate={new Date("01/01/1000")}
            label="Publication Date"
            variant="inline"
            // value={moment.utc(media.publicationDate)}
            value={media.publicationDate}
            // value={dateAdjust(new Date(media.publicationDate))}
            onChange={onFieldChange(["publicationDate"])}
            KeyboardButtonProps={{
              "aria-label": "change date",
            }}
          />

          <Checkbox
            checked={media.publicationDateCirca}
            label="Circa"
            onChange={onFieldChange(["publicationDateCirca"])}
          />
        </div>
        <Autocomplete
          className={styles.field}
          direction="down"
          label="License Type"
          multiple={false}
          showSuggestionsWhenValueIsSet={true}
          onChange={onFieldChange(["licenseTypeCode"])}
          source={getAutocompleteSource(licenseTypes)}
          value={licenseTypeCodePath(media)}
        />
        <Checkbox
          checked={
            media.allowDownload ||
            alwaysDownload(licenseTypeCodePath(media), licenseTypes)
          }
          disabled={alwaysDownload(licenseTypeCodePath(media), licenseTypes)}
          label="Allow this content to be downloaded (Open license types ALWAYS allow download)"
          onChange={onFieldChange(["allowDownload"])}
        />
        <Checkbox
          checked={media.thumbnailLocked}
          label="Thumbnail Locked"
          onChange={onFieldChange(["thumbnailLocked"])}
        />
        <Input
          className={styles.field}
          label="Attribution (Optional)"
          onChange={onFieldChange(["attribution"])}
          value={media.attribution}
        />
        <Input
          className={styles.field}
          label="Source URL (Optional)"
          onChange={onFieldChange(["sourceUrl"])}
          value={media.sourceUrl}
        />
        <Input
          className={styles.field}
          label="Purchase Title (Optional)"
          onChange={onFieldChange(["purchaseLink", "title"])}
          value={media.purchaseLink ? media.purchaseLink.title : undefined}
        />
        <Input
          className={styles.field}
          label="Purchase URL (Optional)"
          onChange={onFieldChange(["purchaseLink", "url"])}
          value={media.purchaseLink ? media.purchaseLink.url : undefined}
        />
        <Input
          className={styles.field}
          label="Content Provider Collection (Optional)"
          onChange={onFieldChange(["contentProviderCollection"])}
          value={media.contentProviderCollection}
        />
      </div>
    </div>
  );
};

const mapStateToProps = applySpec({
  languages: getLanguages,
  licenseTypes: getLicenseTypes,
  media: getEditMediaItem,
  errors: getEditMediaErrors,
  thumbnailProcessing: getThumbnailDialogProcessing,
});

const mapDispatchToProps = dispatch => ({
  onFieldChange: path => value => dispatch(mediaItemFieldChanged(path, value)),
  onFieldBlur: (field, value) => dispatch(blurMediaItemField(field, value)),
  onEditThumbnailClicked: () => dispatch(showEditThumbnailDialog()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditMediaGeneralTab);
